'use client';

/**
 * ConsumptionPricingTab - Communication & AI Token Pricing Management
 *
 * Two sections:
 * 1. Communication Channel Pricing (SMS, email, voice AI, voice calls, WhatsApp, social media)
 * 2. AI Token Pricing (per-service token rates)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare, Mail, Phone, PhoneCall, MessageCircle, Share2,
  Bot, Plus, Edit2, Save, X, Loader2, AlertCircle, DollarSign,
} from 'lucide-react';
import OrgUsageRatesPanel from './OrgUsageRatesPanel';
const PLATFORM_BACKEND_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PLATFORM_API_URL) || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_WEAVEIN_API_URL) || 'https://weavein-api.onrender.com';

const getAuthHeaders = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  const cboId = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
  const tenantSlug = typeof window !== 'undefined' ? localStorage.getItem('platform_tenant_slug') : null;
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (cboId) headers['X-CBO-ID'] = cboId;
  if (tenantSlug) headers['X-Tenant-ID'] = tenantSlug;
  return headers;
};

async function fetchAPI<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = { 'Content-Type': 'application/json', ...getAuthHeaders(), ...options.headers };
  const res = await fetch(`${PLATFORM_BACKEND_URL}/api/platform/v1/revops${path}`, { ...options, headers });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
  return res.json();
}

interface CommPricing {
  id: string;
  channel: string;
  channel_display_name: string;
  unit_name: string;
  cost_per_unit: number;
  included_units_per_month: number;
  included_period?: 'month' | 'year';
  covered_by_platform: boolean;
  default_customer_threshold: number;
  overage_rate: number | null;
  target_organization_id?: number | null;
  is_active: boolean;
}

interface AiPricing {
  id: string;
  service_name: string;
  service_code: string;
  description: string;
  model_name: string;
  model_provider: string;
  input_rate_per_million: number;
  output_rate_per_million: number;
  included_credits_per_month: number;
  overage_multiplier: number;
  target_organization_id?: number | null;
  is_active: boolean;
}

const CHANNEL_ICONS: Record<string, React.ReactNode> = {
  sms: <MessageSquare className="w-4 h-4 text-green-600" />,
  email: <Mail className="w-4 h-4 text-blue-600" />,
  voice_ai: <Bot className="w-4 h-4 text-purple-600" />,
  voice_call: <Phone className="w-4 h-4 text-orange-600" />,
  whatsapp: <MessageCircle className="w-4 h-4 text-emerald-600" />,
  social_media: <Share2 className="w-4 h-4 text-pink-600" />,
};

const CHANNEL_DEFAULTS = [
  { channel: 'sms', display_name: 'SMS Messages', unit_name: 'message', default_cost: 0.05 },
  { channel: 'email', display_name: 'Emails', unit_name: 'email', default_cost: 0.003 },
  { channel: 'voice_ai', display_name: 'Voice AI', unit_name: 'minute', default_cost: 0.15 },
  { channel: 'voice_call', display_name: 'Voice Calls', unit_name: 'minute', default_cost: 0.08 },
  { channel: 'whatsapp', display_name: 'WhatsApp', unit_name: 'message', default_cost: 0.06 },
  { channel: 'social_media', display_name: 'Social Media', unit_name: 'post', default_cost: 0.02 },
];

export default function ConsumptionPricingTab() {
  const [commPricing, setCommPricing] = useState<CommPricing[]>([]);
  const [aiPricing, setAiPricing] = useState<AiPricing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingComm, setEditingComm] = useState<string | null>(null);
  const [editingAi, setEditingAi] = useState<string | null>(null);
  const [commForm, setCommForm] = useState<Partial<CommPricing>>({});
  const [aiForm, setAiForm] = useState<Partial<AiPricing>>({});
  const [saving, setSaving] = useState(false);
  const [showAddComm, setShowAddComm] = useState(false);
  const [showAddAi, setShowAddAi] = useState(false);
  const [orgs, setOrgs] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    fetch(`${PLATFORM_BACKEND_URL}/api/cbos`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setOrgs((Array.isArray(d) ? d : d.cbos || []).map((o: any) => ({ id: o.id, name: o.name }))))
      .catch(() => setOrgs([]));
  }, []);
  const orgName = (id?: number | null) => (id ? (orgs.find((o) => o.id === id)?.name || `Org ${id}`) : 'All orgs (template)');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [commData, aiData] = await Promise.all([
        // scope=all => templates AND every per-org assignment (operator view)
        fetchAPI<{ communication_pricing: CommPricing[] }>('/communication-pricing?scope=all'),
        fetchAPI<{ ai_token_pricing: AiPricing[] }>('/ai-token-pricing?scope=all'),
      ]);
      setCommPricing(commData.communication_pricing || []);
      setAiPricing(aiData.ai_token_pricing || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const saveCommPricing = async (isNew: boolean) => {
    setSaving(true);
    try {
      const method = isNew ? 'POST' : 'PUT';
      const path = isNew ? '/communication-pricing' : `/communication-pricing/${editingComm}`;
      await fetchAPI(path, { method, body: JSON.stringify(commForm) });
      await loadData();
      setEditingComm(null);
      setShowAddComm(false);
      setCommForm({});
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveAiPricing = async (isNew: boolean) => {
    setSaving(true);
    try {
      const method = isNew ? 'POST' : 'PUT';
      const path = isNew ? '/ai-token-pricing' : `/ai-token-pricing/${editingAi}`;
      await fetchAPI(path, { method, body: JSON.stringify(aiForm) });
      await loadData();
      setEditingAi(null);
      setShowAddAi(false);
      setAiForm({});
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-indigo-600 animate-spin mr-2" />
        <span className="text-gray-600">Loading pricing configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4 text-red-400" /></button>
        </div>
      )}

      <OrgUsageRatesPanel fetchAPI={fetchAPI} orgs={orgs} onChanged={loadData} />

      {/* Communication Channel Pricing */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-600" />
              Communication Channel Pricing
            </h2>
            <p className="text-sm text-gray-500">
              Set per-unit pricing for outreach communications. Configure platform-covered thresholds and overage rates.
            </p>
          </div>
          <button
            onClick={() => {
              setShowAddComm(true);
              setCommForm({ is_active: true, included_units_per_month: 0, default_customer_threshold: 0, covered_by_platform: false });
            }}
            className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Channel
          </button>
        </div>

        <div className="bg-white border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Channel</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Assigned to</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Unit</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Cost/Unit</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Included</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Overage Rate</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Platform Covered</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {commPricing.map(cp => (
                <tr key={cp.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 flex items-center gap-2">
                    {CHANNEL_ICONS[cp.channel] || <DollarSign className="w-4 h-4" />}
                    <span className="font-medium">{cp.channel_display_name || cp.channel}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {cp.target_organization_id
                      ? <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs">{orgName(cp.target_organization_id)}</span>
                      : <span className="text-gray-400 text-xs">All orgs (template)</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{cp.unit_name}</td>
                  <td className="px-4 py-3 text-right font-mono">${cp.cost_per_unit.toFixed(4)}</td>
                  <td className="px-4 py-3 text-right">{cp.included_units_per_month.toLocaleString()}<span className="text-gray-400">/{cp.included_period === 'year' ? 'yr' : 'mo'}</span></td>
                  <td className="px-4 py-3 text-right font-mono">{cp.overage_rate != null ? `$${cp.overage_rate.toFixed(4)}` : '-'}</td>
                  <td className="px-4 py-3 text-center">
                    {cp.covered_by_platform ? (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">Yes</span>
                    ) : (
                      <span className="text-gray-400 text-xs">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => {
                        setEditingComm(cp.id);
                        setCommForm(cp);
                      }}
                      className="text-gray-400 hover:text-indigo-600"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {commPricing.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    No communication pricing configured. Add channels to start tracking usage costs.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Add/Edit Communication Pricing Modal */}
        {(showAddComm || editingComm) && (
          <div className="mt-4 border rounded-xl p-5 bg-white space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-medium">{showAddComm ? 'Add Channel Pricing' : 'Edit Channel Pricing'}</h3>
              <button onClick={() => { setShowAddComm(false); setEditingComm(null); setCommForm({}); }}>
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-gray-500">Channel</label>
                <select
                  value={commForm.channel || ''}
                  onChange={e => {
                    const channel = e.target.value;
                    const preset = CHANNEL_DEFAULTS.find(c => c.channel === channel);
                    // Auto-fill the required unit_name/cost_per_unit (and display name)
                    // from the channel preset so the payload is always valid; the
                    // user can still override any of them below.
                    setCommForm(p => ({
                      ...p,
                      channel,
                      channel_display_name: p.channel_display_name || preset?.display_name || '',
                      unit_name: p.unit_name || preset?.unit_name || '',
                      cost_per_unit: p.cost_per_unit ?? preset?.default_cost ?? 0,
                    }));
                  }}
                  className="w-full px-2 py-1.5 border rounded text-sm"
                >
                  <option value="">Select...</option>
                  {CHANNEL_DEFAULTS.map(c => (
                    <option key={c.channel} value={c.channel}>{c.display_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Assign to organization</label>
                <select
                  value={commForm.target_organization_id ?? ''}
                  onChange={e => setCommForm(p => ({ ...p, target_organization_id: e.target.value ? Number(e.target.value) : null }))}
                  className="w-full px-2 py-1.5 border rounded text-sm"
                >
                  <option value="">All orgs (template)</option>
                  {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Display Name</label>
                <input type="text" value={commForm.channel_display_name || ''} onChange={e => setCommForm(p => ({ ...p, channel_display_name: e.target.value }))} className="w-full px-2 py-1.5 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Unit Name</label>
                <input type="text" value={commForm.unit_name || ''} onChange={e => setCommForm(p => ({ ...p, unit_name: e.target.value }))} className="w-full px-2 py-1.5 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Cost per Unit ($)</label>
                <input type="number" step="0.0001" value={commForm.cost_per_unit || ''} onChange={e => setCommForm(p => ({ ...p, cost_per_unit: parseFloat(e.target.value) || 0 }))} className="w-full px-2 py-1.5 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Included Units (free)</label>
                <input type="number" value={commForm.included_units_per_month || 0} onChange={e => setCommForm(p => ({ ...p, included_units_per_month: parseInt(e.target.value) || 0 }))} className="w-full px-2 py-1.5 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Allowance period</label>
                <select value={commForm.included_period || 'month'} onChange={e => setCommForm(p => ({ ...p, included_period: e.target.value as 'month' | 'year' }))} className="w-full px-2 py-1.5 border rounded text-sm">
                  <option value="month">Per month</option>
                  <option value="year">Per year</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500">Customer Threshold</label>
                <input type="number" value={commForm.default_customer_threshold || 0} onChange={e => setCommForm(p => ({ ...p, default_customer_threshold: parseInt(e.target.value) || 0 }))} className="w-full px-2 py-1.5 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Overage Rate ($/unit, 0 = free)</label>
                <input type="number" step="0.0001" value={commForm.overage_rate ?? ''} onChange={e => setCommForm(p => ({ ...p, overage_rate: e.target.value === '' ? null : (parseFloat(e.target.value) || 0) }))} className="w-full px-2 py-1.5 border rounded text-sm" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={commForm.covered_by_platform || false} onChange={e => setCommForm(p => ({ ...p, covered_by_platform: e.target.checked }))} className="rounded" />
                  <span className="text-sm">Platform Covered</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowAddComm(false); setEditingComm(null); }} className="px-3 py-1.5 text-sm bg-gray-100 rounded-lg">Cancel</button>
              <button
                onClick={() => saveCommPricing(showAddComm)}
                disabled={saving || !commForm.channel || !commForm.unit_name || commForm.cost_per_unit == null}
                className="inline-flex items-center px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg disabled:bg-gray-400"
              >
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                Save
              </button>
            </div>
          </div>
        )}
      </section>

      {/* AI Token Pricing */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Bot className="w-5 h-5 text-purple-600" />
              AI Token Pricing
            </h2>
            <p className="text-sm text-gray-500">
              Configure token consumption pricing for AI services. Set input/output rates per million tokens.
            </p>
          </div>
          <button
            onClick={() => {
              setShowAddAi(true);
              setAiForm({ is_active: true, overage_multiplier: 1.0, included_credits_per_month: 0, input_rate_per_million: 0, output_rate_per_million: 0 });
            }}
            className="inline-flex items-center px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Service
          </button>
        </div>

        <div className="bg-white border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Service</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Assigned to</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Model</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Input Rate/1M</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Output Rate/1M</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Included Credits/mo</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Overage Mult.</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {aiPricing.map(ap => (
                <tr key={ap.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium">{ap.service_name}</div>
                    <div className="text-xs text-gray-400">{ap.service_code}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {ap.target_organization_id
                      ? <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs">{orgName(ap.target_organization_id)}</span>
                      : <span className="text-gray-400 text-xs">All orgs (template)</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {ap.model_name || '-'}
                    {ap.model_provider && <span className="text-xs text-gray-400 ml-1">({ap.model_provider})</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">${ap.input_rate_per_million.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-mono">${ap.output_rate_per_million.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">${ap.included_credits_per_month.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">{ap.overage_multiplier}x</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => { setEditingAi(ap.id); setAiForm(ap); }}
                      className="text-gray-400 hover:text-indigo-600"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {aiPricing.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    No AI token pricing configured. Add services to track AI consumption costs.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Add/Edit AI Pricing */}
        {(showAddAi || editingAi) && (
          <div className="mt-4 border rounded-xl p-5 bg-white space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-medium">{showAddAi ? 'Add AI Service Pricing' : 'Edit AI Service Pricing'}</h3>
              <button onClick={() => { setShowAddAi(false); setEditingAi(null); setAiForm({}); }}>
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-gray-500">Service Name</label>
                <input type="text" value={aiForm.service_name || ''} onChange={e => setAiForm(p => ({ ...p, service_name: e.target.value }))} className="w-full px-2 py-1.5 border rounded text-sm" placeholder="e.g., Document Analysis" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Service Code</label>
                <input type="text" value={aiForm.service_code || ''} onChange={e => setAiForm(p => ({ ...p, service_code: e.target.value }))} className="w-full px-2 py-1.5 border rounded text-sm" placeholder="e.g., doc_analysis" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Model Name</label>
                <input type="text" value={aiForm.model_name || ''} onChange={e => setAiForm(p => ({ ...p, model_name: e.target.value }))} className="w-full px-2 py-1.5 border rounded text-sm" placeholder="e.g., gpt-4o" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Model Provider</label>
                <input type="text" value={aiForm.model_provider || ''} onChange={e => setAiForm(p => ({ ...p, model_provider: e.target.value }))} className="w-full px-2 py-1.5 border rounded text-sm" placeholder="e.g., openai" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Input Rate / 1M tokens ($)</label>
                <input type="number" step="0.01" value={aiForm.input_rate_per_million || ''} onChange={e => setAiForm(p => ({ ...p, input_rate_per_million: parseFloat(e.target.value) || 0 }))} className="w-full px-2 py-1.5 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Output Rate / 1M tokens ($)</label>
                <input type="number" step="0.01" value={aiForm.output_rate_per_million || ''} onChange={e => setAiForm(p => ({ ...p, output_rate_per_million: parseFloat(e.target.value) || 0 }))} className="w-full px-2 py-1.5 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Included Credits/mo ($)</label>
                <input type="number" step="0.01" value={aiForm.included_credits_per_month || ''} onChange={e => setAiForm(p => ({ ...p, included_credits_per_month: parseFloat(e.target.value) || 0 }))} className="w-full px-2 py-1.5 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Overage Multiplier</label>
                <input type="number" step="0.1" value={aiForm.overage_multiplier || 1.0} onChange={e => setAiForm(p => ({ ...p, overage_multiplier: parseFloat(e.target.value) || 1.0 }))} className="w-full px-2 py-1.5 border rounded text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Description</label>
                <input type="text" value={aiForm.description || ''} onChange={e => setAiForm(p => ({ ...p, description: e.target.value }))} className="w-full px-2 py-1.5 border rounded text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowAddAi(false); setEditingAi(null); }} className="px-3 py-1.5 text-sm bg-gray-100 rounded-lg">Cancel</button>
              <button
                onClick={() => saveAiPricing(showAddAi)}
                disabled={saving || !aiForm.service_name || !aiForm.service_code}
                className="inline-flex items-center px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg disabled:bg-gray-400"
              >
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                Save
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
